import type { User } from '../store/types';

/**
 * Application permissions aligned with the auth spec's RBAC model.
 * Backend enforces these via AWS Verified Permissions (Cedar).
 * Frontend uses them only for UX — never as a security boundary.
 */
export type Permission =
  | 'service:create'
  | 'service:update'
  | 'service:delete'
  | 'team:create'
  | 'team:update'
  | 'team:delete'
  | 'team:manage-members'
  | 'stakeholder:create'
  | 'stakeholder:update'
  | 'stakeholder:delete'
  | 'capability:create'
  | 'capability:update'
  | 'capability:delete'
  | 'technology:create'
  | 'technology:update'
  | 'technology:delete'
  | 'technology-capability:create'
  | 'technology-capability:update'
  | 'technology-capability:delete'
  | 'technology-domain:create'
  | 'technology-domain:update'
  | 'technology-domain:delete'
  | 'business-environment-factor:create'
  | 'business-environment-factor:update'
  | 'business-environment-factor:delete'
  | 'business-process:create'
  | 'business-process:update'
  | 'business-process:delete'
  | 'physical-process:create'
  | 'physical-process:update'
  | 'physical-process:delete'
  | 'business-objective:create'
  | 'business-objective:update'
  | 'business-objective:delete'
  | 'business-unit:create'
  | 'business-unit:update'
  | 'business-unit:delete'
  | 'maturityFramework:create'
  | 'maturityFramework:update'
  | 'maturityFramework:delete'
  | 'maturityAssessment:create'
  | 'maturityAssessment:update'
  | 'maturityAssessment:delete'
  | 'maturityAssessment:approve'
  | 'tag:create'
  | 'tag:update'
  | 'tag:delete'
  | 'tagCategory:create'
  | 'tagCategory:update'
  | 'tagCategory:delete'
  | 'information:create'
  | 'information:update'
  | 'information:delete'
  | 'meta-model:edit'
  | 'audit:view'
  | 'audit:export'
  | 'analytics:view'
  | 'analytics:export'
  | 'model-package:export'
  | 'model-package:import'
  | 'user:manage'
  | 'user:create'
  | 'user:delete'
  | 'bulk-operations:write'
  | 'bulk-operations:read'
  | 'project:create'
  | 'project:update'
  | 'project:delete'
  | 'diagram:create'
  | 'task:update'
  | 'task:approve';

/**
 * Role names as defined in the auth spec (maps to Okta group claims).
 * Hierarchy: Administrator > TeamAdministrator > ServiceEditor > Read-Only
 *            Administrator > RoleAdministrator > Read-Only
 *            Administrator > MetaModelEditor > Read-Only
 */
type Role =
  | 'Administrator'
  | 'TeamAdministrator'
  | 'RoleAdministrator'
  | 'ServiceEditor'
  | 'CapabilityAdministrator'
  | 'BusinessEditor'
  | 'MetaModelEditor'
  | 'Read-Only';

const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  Administrator: [
    'service:create',
    'service:update',
    'service:delete',
    'team:create',
    'team:update',
    'team:delete',
    'team:manage-members',
    'stakeholder:create',
    'stakeholder:update',
    'stakeholder:delete',
    'capability:create',
    'capability:update',
    'capability:delete',
    'technology:create',
    'technology:update',
    'technology:delete',
    'technology-capability:create',
    'technology-capability:update',
    'technology-capability:delete',
    'technology-domain:create',
    'technology-domain:update',
    'technology-domain:delete',
    'business-environment-factor:create',
    'business-environment-factor:update',
    'business-environment-factor:delete',
    'business-process:create',
    'business-process:update',
    'business-process:delete',
    'physical-process:create',
    'physical-process:update',
    'physical-process:delete',
    'business-objective:create',
    'business-objective:update',
    'business-objective:delete',
    'business-unit:create',
    'business-unit:update',
    'business-unit:delete',
    'maturityFramework:create',
    'maturityFramework:update',
    'maturityFramework:delete',
    'maturityAssessment:create',
    'maturityAssessment:update',
    'maturityAssessment:delete',
    'maturityAssessment:approve',
    'tag:create',
    'tag:update',
    'tag:delete',
    'tagCategory:create',
    'tagCategory:update',
    'tagCategory:delete',
    'information:create',
    'information:update',
    'information:delete',
    'meta-model:edit',
    'audit:view',
    'audit:export',
    'analytics:view',
    'analytics:export',
    'model-package:export',
    'model-package:import',
    'user:manage',
    'user:create',
    'user:delete',
    'bulk-operations:write',
    'bulk-operations:read',
    'project:create',
    'project:update',
    'project:delete',
    'diagram:create',
    'task:update',
    'task:approve',
  ],
  TeamAdministrator: [
    'service:create',
    'service:update',
    'service:delete',
    'team:create',
    'team:update',
    'team:delete',
    'team:manage-members',
    'stakeholder:create',
    'stakeholder:update',
    'stakeholder:delete',
    'maturityAssessment:create',
    'maturityAssessment:update',
    'maturityAssessment:delete',
    'audit:view',
    'diagram:create',
    'task:update',
    'task:approve',
  ],
  RoleAdministrator: ['user:manage', 'audit:view'],
  ServiceEditor: [
    'service:create',
    'service:update',
    'service:delete',
    'maturityAssessment:create',
    'maturityAssessment:update',
    'maturityAssessment:delete',
    'tag:create',
    'tag:update',
    'tag:delete',
    'tagCategory:create',
    'tagCategory:update',
    'tagCategory:delete',
    'audit:view',
    'diagram:create',
    'task:update',
    'task:approve',
  ],
  CapabilityAdministrator: [
    'capability:create',
    'capability:update',
    'capability:delete',
    'audit:view',
    'task:update',
    'task:approve',
  ],
  // Business-tier content editor (issue #377). Full CRUD on Business pillar
  // entities; reads (and therefore edge-assignment of other-tier entities on
  // business entities) are open to all roles.
  BusinessEditor: [
    'capability:create',
    'capability:update',
    'capability:delete',
    'stakeholder:create',
    'stakeholder:update',
    'stakeholder:delete',
    'business-environment-factor:create',
    'business-environment-factor:update',
    'business-environment-factor:delete',
    'business-process:create',
    'business-process:update',
    'business-process:delete',
    'physical-process:create',
    'physical-process:update',
    'physical-process:delete',
    'business-objective:create',
    'business-objective:update',
    'business-objective:delete',
    'business-unit:create',
    'business-unit:update',
    'business-unit:delete',
    'audit:view',
    'task:update',
    'task:approve',
  ],
  MetaModelEditor: ['meta-model:edit', 'audit:view'],
  'Read-Only': ['audit:view'],
};

/**
 * Returns true if the user holds a role that grants the requested permission.
 * Accepts both spec role names (e.g. "Administrator") and legacy short names
 * (e.g. "admin") for backwards compatibility during migration.
 */
export function hasPermission(user: User | null, permission: Permission): boolean {
  if (!user) return false;

  return user.roles.some((role) => {
    const normalized = normalizeRole(role);
    if (!normalized) return false;
    // normalized is a validated Role enum value — not user-controlled
    // eslint-disable-next-line security/detect-object-injection
    return ROLE_PERMISSIONS[normalized].includes(permission);
  });
}

/**
 * True when the user holds the Administrator role (any accepted spelling).
 */
export function isAdministrator(user: User | null): boolean {
  if (!user) return false;
  return user.roles.some((role) => normalizeRole(role) === 'Administrator');
}

/**
 * ABAC UX gate for claiming / assigning a service to a team (issue #536) —
 * mirrors backend `canAssignServiceToTeam`. Frontend UX-only; the backend
 * always enforces the real check.
 *
 * Returns true if the user is an Administrator (may assign to any team) or a
 * member of the target team.
 */
export function canAssignServiceToTeam(
  user: User | null,
  targetTeamId: string | null | undefined
): boolean {
  if (!user) return false;
  if (isAdministrator(user)) return true;
  if (!targetTeamId) return false;
  return (user.teams ?? []).includes(targetTeamId);
}

function normalizeRole(role: string): Role | null {
  // Exact spec names
  if (role in ROLE_PERMISSIONS) return role as Role;

  // TID-W Okta group names (must match backend OKTA_GROUP_ROLE_MAP)
  // and legacy short-form aliases used during development
  const aliases: Record<string, Role> = {
    'ea-administrators': 'Administrator',
    'ea-teamadministrators': 'TeamAdministrator',
    'ea-roleadministrators': 'RoleAdministrator',
    'ea-serviceeditors': 'ServiceEditor',
    'ea-capabilityadministrators': 'CapabilityAdministrator',
    'ea-businesseditors': 'BusinessEditor',
    'ea-metamodeleditors': 'MetaModelEditor',
    admin: 'Administrator',
    administrator: 'Administrator',
    teamadministrator: 'TeamAdministrator',
    'team-administrator': 'TeamAdministrator',
    roleadministrator: 'RoleAdministrator',
    'role-administrator': 'RoleAdministrator',
    'service-editor': 'ServiceEditor',
    serviceeditor: 'ServiceEditor',
    architect: 'ServiceEditor',
    'capability-administrator': 'CapabilityAdministrator',
    capabilityadministrator: 'CapabilityAdministrator',
    'business-editor': 'BusinessEditor',
    businesseditor: 'BusinessEditor',
    'metamodel-editor': 'MetaModelEditor',
    metamodeleditor: 'MetaModelEditor',
    'read-only': 'Read-Only',
    readonly: 'Read-Only',
    user: 'Read-Only',
  };

  return aliases[role.toLowerCase()] ?? null;
}

/**
 * ABAC check for assessment create/delete — mirrors backend canManageAssessment.
 *
 * Frontend UX-only gate. Backend always enforces the real check.
 * Returns true if:
 * - user is Administrator
 * - user is the service creator (_createdBy == userId)
 * - user is a member of the service's owning team (teamId in user.teams)
 */
export function canCreateAssessment(
  user: User | null,
  service: { _createdBy?: string; teamId?: string | null } | null | undefined
): boolean {
  if (!user) return false;
  if (hasPermission(user, 'maturityFramework:create')) return true; // Administrator
  if (!service) return false;
  if (service._createdBy === user.id) return true;
  if (service.teamId && user.teams?.includes(service.teamId)) return true;
  return false;
}

/**
 * ABAC check for TagCategory create/update/delete — mirrors backend canManageTagCategory.
 *
 * Frontend UX-only gate. Backend always enforces the real check.
 * Returns true if:
 * - user is Administrator
 * - category is team-scoped AND user is primary contact of the owning team
 */
export function canManageTagCategory(
  user: User | null,
  category: { scope: 'global' | 'team'; scopeOwnerId?: string } | null | undefined
): boolean {
  if (!user) return false;
  if (!category) return false;
  if (
    hasPermission(user, 'tagCategory:create') &&
    user.roles.some((r) => normalizeRole(r) === 'Administrator')
  )
    return true;
  if (category.scope === 'global') return false;
  if (category.scope === 'team' && category.scopeOwnerId) {
    if (!hasPermission(user, 'tagCategory:create')) return false;
    return (user.primaryContactForTeams ?? []).includes(category.scopeOwnerId);
  }
  return false;
}

/**
 * ABAC check for Tag create — mirrors backend canManageTag.
 *
 * Frontend UX-only gate. Backend always enforces the real check.
 * Returns true if user can write to the tag's parent category.
 */
export function canCreateTagInCategory(
  user: User | null,
  category: { scope: 'global' | 'team'; scopeOwnerId?: string } | null | undefined
): boolean {
  return canManageTagCategory(user, category);
}
